import {
  DiagnosticReport,
  Encounter,
  Location,
  Organization,
  Practitioner,
  Procedure,
} from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { toTitleCase } from "@metriport/shared";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import { getImagingReportIdsFromProcedures } from "../../../shared-components/fhir-sections/reports/note-type-utils";
import { extractNotesFromDiagnosticReport } from "../../utils/note-processing";
import { TableRow } from "../../utils/tables-metadata";
import { getImagingModalityFromProcedures } from "./imaging-modality-utils";
import { getLoincCodeClass, isLoincCoding } from "../../utils/loinc/loinc-code-class-lookup";
import { isImagingReportBasedOnDisplayValueParsing } from "../../featured-section/rules/imaging-rules";
import { getReportTypeByLoinc } from "../../../shared-components/fhir-sections/reports/table-data";

export type ImagingReportRowData = TableRow & {
  id: string;
  imagingModality: string;
  bodyRegion: string;
  associatingDiagnosis: string;
  location: string;
  date: string;
  originalData: Smart<DiagnosticReport>;
  noteData?: {
    fullText: string;
  };
};

export function getImagingReportRows({
  bundle,
}: {
  bundle: FhirBundleSdk;
}): ImagingReportRowData[] {
  const diagnosticReports = bundle.getDiagnosticReports();
  const procedures = bundle.getProcedures();
  const imagingReportIds = getImagingReportIdsFromProcedures(procedures);

  const rows = diagnosticReports
    .filter(report => isImagingReport(report, imagingReportIds, bundle))
    .map(report => buildImagingReportRow(report, bundle, procedures))
    .filter((row): row is ImagingReportRowData => row !== null);

  return rows.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));
}

export function isImagingReport(
  report: Smart<DiagnosticReport>,
  imagingReportIds: Set<string>,
  bundle: FhirBundleSdk
): boolean {
  if (report.id && imagingReportIds.has(report.id)) {
    return true;
  }

  const loincCoding = report.code?.coding?.find(isLoincCoding);
  if (
    loincCoding &&
    loincCoding.code &&
    getLoincCodeClass(loincCoding.code)?.abbreviation === "RAD"
  ) {
    return true;
  }

  if (isImagingReportBasedOnDisplayValueParsing(report)) {
    return true;
  }

  // Get encounter to check encounter-based practitioners and organizations
  const encounterId = report.encounter?.reference?.split("/").pop();
  const encounter = encounterId ? bundle.getResourceById<Encounter>(encounterId) : undefined;

  // Check practitioners from both diagnostic report AND encounter
  const reportPractitioners = getReportPractitioners(report);
  const encounterPractitioners = encounter ? getEncounterPractitioners(encounter) : [];
  const allPractitioners = [...reportPractitioners, ...encounterPractitioners];

  for (const practitioner of allPractitioners) {
    if (practitioner.qualification) {
      for (const qualification of practitioner.qualification) {
        if (qualification.code?.text) {
          const qualificationText = qualification.code.text.toLowerCase();
          if (qualificationText.includes("radiology") || qualificationText.includes("imaging")) {
            return true;
          }
        }
        if (qualification.code?.coding) {
          for (const coding of qualification.code.coding) {
            const display = coding.display?.toLowerCase();
            if (display && (display.includes("radiology") || display.includes("imaging"))) {
              return true;
            }
          }
        }
      }
    }
  }

  // Check organizations from both diagnostic report AND encounter
  const reportOrganizations = getReportOrganizations(report);
  const encounterOrganizations = encounter ? getEncounterOrganizations(encounter) : [];
  const allOrganizations = [...reportOrganizations, ...encounterOrganizations];

  for (const organization of allOrganizations) {
    const name = organization.name?.toLowerCase();
    if (name && (name.includes("radiology") || name.includes("imaging"))) {
      return true;
    }
  }

  return false;
}

function getReportPractitioners(report: Smart<DiagnosticReport>): Smart<Practitioner>[] {
  return report
    .getPerformers()
    .filter((p): p is Smart<Practitioner> => p.resourceType === "Practitioner");
}

function getReportOrganizations(report: Smart<DiagnosticReport>): Smart<Organization>[] {
  return report
    .getPerformers()
    .filter((o): o is Smart<Organization> => o.resourceType === "Organization");
}

function buildImagingReportRow(
  diagnosticReport: Smart<DiagnosticReport>,
  bundle: FhirBundleSdk,
  allProcedures: Procedure[]
): ImagingReportRowData | null {
  // Skip reports without actual note content (presentedForm)
  if (!diagnosticReport.presentedForm) {
    return null;
  }

  const loincCoding = diagnosticReport.code?.coding?.find(isLoincCoding);
  const typeOfReport = getReportTypeByLoinc([diagnosticReport]);
  const imagingDisplay = loincCoding?.display ?? typeOfReport ?? "-";

  // Get encounter using SDK's built-in O(1) lookup
  const encounterId = diagnosticReport.encounter?.reference?.split("/").pop();
  const encounter = encounterId ? bundle.getResourceById<Encounter>(encounterId) : undefined;

  // Get organizations and locations from encounter (if it exists)
  const organizations = encounter ? getEncounterOrganizations(encounter) : [];
  const locations = encounter ? getEncounterLocations(encounter) : [];

  // Get linked procedures (those that reference this diagnostic report)
  const linkedProcedures = allProcedures.filter(p =>
    p.report?.some(ref => ref.reference?.split("/").pop() === diagnosticReport.id)
  );

  // Extract imaging-specific data from procedures
  const imagingModality = getImagingModalityFromProcedures(linkedProcedures);
  const bodyRegion = getBodyRegionFromProcedures(linkedProcedures);
  const associatingDiagnosis = getAssociatingDiagnosisFromProcedures(linkedProcedures);

  // Extract note text
  const notes = extractNotesFromDiagnosticReport(diagnosticReport);
  const fullNoteText = notes.join(" ");

  const locationNames = locations
    .map(l => l.name)
    .filter(Boolean)
    .join(", ");
  const orgNames = organizations
    .map(o => o.name)
    .filter(Boolean)
    .join(", ");

  // Create synthetic encounter for orphaned reports
  const effectiveEncounter = encounter ?? createSyntheticEncounter(diagnosticReport);

  return {
    id: `${effectiveEncounter.id}-${diagnosticReport.id}`,
    imagingDisplay: imagingDisplay,
    imagingModality,
    bodyRegion,
    associatingDiagnosis,
    location: locationNames || orgNames || "-",
    date: effectiveEncounter.period?.start
      ? dayjs(effectiveEncounter.period.start).format(ISO_DATE)
      : "-",
    originalData: diagnosticReport,
    noteData: fullNoteText ? { fullText: fullNoteText } : undefined,
  };
}

function getBodyRegionFromProcedures(procedures: Procedure[]): string {
  const bodyRegions = new Set<string>();
  procedures.forEach(procedure => {
    const bodySite = procedure.bodySite?.[0];
    if (!bodySite) return;
    if (bodySite.text) {
      bodyRegions.add(cleanBodyRegionText(bodySite.text));
      return;
    }
    const display = bodySite.coding?.[0]?.display;
    if (display) bodyRegions.add(cleanBodyRegionText(display));
  });
  return bodyRegions.size > 0 ? Array.from(bodyRegions).join(", ") : "-";
}

function cleanBodyRegionText(text: string): string {
  return text
    .replace(/\s*\(body structure\)\s*$/i, "")
    .replace(/\s*\(specimen\)\s*$/i, "")
    .replace(/\s+structure$/i, "")
    .trim();
}

function getAssociatingDiagnosisFromProcedures(procedures: Procedure[]): string {
  const diagnoses = new Set<string>();
  procedures.forEach(procedure => {
    procedure.reasonCode?.forEach(reason => {
      if (reason.text) {
        diagnoses.add(reason.text);
        return;
      }
      reason.coding?.forEach(coding => {
        if (coding.display) diagnoses.add(coding.display);
      });
    });
  });
  return diagnoses.size > 0 ? toTitleCase(Array.from(diagnoses).join(", ")) : "-";
}

function getEncounterPractitioners(encounter: Smart<Encounter>): Smart<Practitioner>[] {
  return encounter
    .getParticipants()
    .filter((p): p is Smart<Practitioner> => p.resourceType === "Practitioner");
}

function getEncounterOrganizations(encounter: Smart<Encounter>): Smart<Organization>[] {
  const org = encounter.getServiceProvider();
  return org ? [org] : [];
}

function getEncounterLocations(encounter: Smart<Encounter>): Smart<Location>[] {
  return encounter.getLocation();
}

function createSyntheticEncounter(diagnosticReport: DiagnosticReport): Encounter {
  return {
    resourceType: "Encounter",
    id: diagnosticReport.id,
    period: diagnosticReport.effectivePeriod ?? { start: diagnosticReport.effectiveDateTime },
  };
}
